import { useEffect } from "react";
import AlbanyHeroSection from "./components/HeroSection";
import AlbanyStickyNav from "./components/StickyNav";
import AlbanyProcessingTimeSection from "./components/ProcessingTimeSection";
import AlbanyServicesSection from "./components/ServicesSection";
import AlbanyLocalAreaSection from "./components/LocalAreaSection";
import AlbanyTestimonialsSection from "./components/TestimonialsSection";
import AlbanyAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import AlbanyComparisonSection from "./components/ComparisonSection";
import AlbanyMistakesSection from "./components/MistakesSection";
import AlbanyHowItWorksSection from "./components/HowItWorksSection";
import AlbanyFAQSection, { faqs } from "./components/FAQSection";
import AlbanyHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import AlbanyFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function AlbanyPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Albany GA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/albany-ga/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Albany, GA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving southwest Georgia travelers. Get your emergency passport fast!");
    setMeta("geo.placename", "Albany, Georgia");
    setMeta("geo.region", "US-GA");
    setMeta("geo.position", "31.5785;-84.1557");
    setMeta("ICBM", "31.5785, -84.1557");
    setMeta("keywords", "expedited passport, emergency passport, Albany passport, same day passport Georgia, fast passport Albany GA");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Georgia", item: "https://fastpassportcenter.com/georgia/" },
          { "@type": "ListItem", position: 3, name: "Albany Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Albany — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Albany and southwest Georgia travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Albany",
          containedInPlace: {
            "@type": "State",
            name: "Georgia",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Albany",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <AlbanyHeroSection />
      <AlbanyStickyNav />
      <AlbanyProcessingTimeSection />
      <AlbanyServicesSection />
      <AlbanyLocalAreaSection />
      <AlbanyTestimonialsSection />
      <AlbanyAcceptanceFacilitiesSection />
      <AlbanyComparisonSection />
      <AlbanyMistakesSection />
      <AlbanyHowItWorksSection />
      <AlbanyFAQSection />
      <AlbanyHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/albany-ga" state="GA" />
      <AlbanyFinalCTA />
    </div>
  );
}